import { Component, h, Prop, State, Watch, Host, Element, Listen } from '@stencil/core';

interface Contact {
    _id: string;
    fs_date: number;
    fs_project_id: number;
    fs_project_name: string;
    fs_revision_id: number;
    fs_reference_name: string;
    fs_object_type: string;
    fs_metadata: unknown;
    fs_uid_type: string;
    fs_scheduler_date: number;
    fs_id: string;
    fs_language: string;
    contactMainBtnTitle: string;
    contactMainBtnText: string;
    contactMainBtnImageSrc: string;
    chatBtnTitle: string;
    chatBtnText: string;
    entranceTitle: string;
    entranceText1: string;
    entranceText2: string;
    contactTitle: string;
    contactText: string;
    firstPhoneNumber: string;
    firstPhoneTitle: string;
    firstPhoneHelpText: string;
    secondPhoneNumber: string;
    secondPhoneTitle: string;
    secondPhoneHelpText: string;
    thirdPhoneNumber: string;
    thirdPhoneTitle: string;
    thirdPhoneHelpText: string;
    apiBaseUrl: string;
    apiKey: string;
    localization: { language: string; region: string };
    externalZipcodeSearch: string;
    externalZipcodeInputName: string;
    placeholder: string;
    recruitingTitle: string;
    recruitingText: string;
    recruitingFirstPhoneNumber: string;
    recruitingFirstPhoneTitle: string;
    recruitingFirstPhoneHelpText: string;
    recruitingSecondPhoneNumber: string;
    recruitingSecondPhoneTitle: string;
    recruitingSecondPhoneHelpText: string;
    recruitingThirdPhoneNumber: string;
    recruitingThirdPhoneTitle: string;
    recruitingThirdPhoneHelpText: string;
    links: { link: Link; tracking: Tracking }[];
    ariaLabelDownload: string;
    ariaLabelContact: string;
    ariaLabelScrollToTop: string;
    downloads: {
        downloadText: string;
        downloadTitle: string;
        buttonText: string;
        linksHeadline: string;
        linksText: string;
        downloadTypes: DownloadType[];
    };
    whatsapp: {
        headline: string;
        text: string;
        items: Array<{
            text?: string;
            img?: {
                src: string;
                alt: string;
                title: string;
            };
        }>;
    };
    searchInputLabel: string;
    tipLabel: string;
    tipOptOutLabel: string;
    tipDuration: number;
    tipApiUrl: string;
}

interface Link {
    fragment: string;
    href: string;
    target: string;
    text: string;
    title: string;
}

interface Tracking {
    action: string;
    category: string;
    object: string;
}

interface DownloadType {
    name: string;
    value: string;
}

interface Tip {
    start: number;
    end: number;
    button_text: string;
    button_link: string;
    headline: string;
    image_alt: string;
    image_src: string;
    image_title: string;
    link_target: string;
    text: string;
    title: string;
    pages: string[];
    id: number;
}

interface Tips {
    fs_date: number;
    tips: Tip[];
}

@Component({
    tag: 'sew-ws-floating-buttons-contact-options',
    styleUrl: 'sew-ws-floating-buttons-contact-options.scss',
    shadow: true,
})
export class SewWsFloatingButtonsContactOptions {
    @Element() host: HTMLSewWsFloatingButtonsContactOptionsElement;
    @Prop() contactApiUrl: string;
    @Prop() linkBtnText: string;
    @Prop() linkBtnLink: string;
    @Prop() linkBtnIcon: string;
    @Prop() enableDownloads: boolean;
    @Prop() enableTips: boolean;
    @Prop() enableLinkButton: boolean;
    @Prop() enableIdleAnimation: boolean;
    @Prop() enableRecruiting: boolean = true;
    @Prop() enableChat: boolean;

    @State() data: Contact;
    @State() enableWhatsapp: boolean;
    @State() loadGoogleApi = false;
    @State() isScrollToTopVisible = false;
    @State() scrolled = false;
    @State() tips: Tips;
    @State() currentTip: Tip;
    @State() webchatChecked = false;
    @State() webchatInitialized = false;

    @Watch('contactApiUrl')
    updateData(): void {
        this.fetchData();
    }

    @Listen('goBack')
    goBackHandler(): void {
        this.setActive('overview');
    }

    @Listen('closeOption')
    closeOptionHandler(): void {
        this.flyooutElement.classList.remove('open');
    }

    @Listen('resize', { target: 'window' })
    onResize(): void {
        this.host.style.setProperty('--current-client-width', document.body.clientWidth + 'px');
    }

    @Listen('scroll', { target: 'window' })
    onScroll(): void {
        if (window.scrollY > 0) {
            this.scrolled = true;

            if (this.lastScrollPosition > window.scrollY) {
                this.isScrollToTopVisible = true;
            } else {
                this.isScrollToTopVisible = false;
            }
        } else {
            this.scrolled = false;
            this.isScrollToTopVisible = false;
        }

        this.lastScrollPosition = window.scrollY;
    }

    private flyooutElement: HTMLElement;
    private lastScrollPosition = 0;
    private hasBeenShown = false;
    private webchatToggleBtnRef: HTMLButtonElement;
    private initCheckInterval;

    private fetchData = async () => {
        this.contactApiUrl.length > 0 &&
            (await fetch(this.contactApiUrl)
                .then(
                    res => res.json(),
                    err => console.error(err)
                )
                .then(json => {
                    this.data = json['_embedded']['rh:doc'][0];

                    this.enableWhatsapp = !!this.data.whatsapp;
                })
                .catch(err => {
                    console.error(err);
                }));
    };

    private getConsent(): boolean {
        return document.cookie.indexOf('googlemaps-opt-in=true') !== -1;
    }

    componentWillLoad(): void {
        this.fetchData().then(() => {
            this.enableTips &&
                this.data.tipApiUrl &&
                this.fetchTips().then(() => {
                    if (
                        this.enableTips &&
                        this.currentTip &&
                        !localStorage.getItem('optOutHighlight') &&
                        !localStorage.getItem('optOutTipPopup')?.includes(String(this.currentTip.id))
                    ) {
                        this.hasBeenShown = true;
                        this.tipInitialTeaser(this.data.tipDuration);
                    }
                });
        });
    }

    componentDidLoad(): void {
        this.onResize();
        setInterval(this.checkChatInitialization, 500);
    }

    private checkChatInitialization = () => {
        const chatContainer = document.querySelector('.webchat-toggle-button');
        if (chatContainer) {
            this.webchatToggleBtnRef = chatContainer as HTMLButtonElement;
            this.webchatInitialized = true;
            clearInterval(this.initCheckInterval);
        }
        this.webchatChecked = true;
    };

    private setActive(active: string): void {
        const flyout = this.host.shadowRoot.querySelector('.flyout') as HTMLElement;
        Array.from(flyout.children).forEach((child: HTMLElement) => {
            if (child.classList.contains(active)) {
                child.classList.add('active');
                flyout.style.height = child.offsetHeight + 'px';
            } else {
                child.classList.remove('active');
            }
        });
    }

    private scrollToTop = (): void => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    private optOutInitialTipAnimation = (): void => {
        localStorage.setItem('optOutHighlight', 'true');
        this.flyooutElement.classList.remove('open');
    };

    private optOutCurrentTipPopup = (): void => {
        let storageValue = localStorage.getItem('optOutTipPopup');

        if (!storageValue?.includes(String(this.currentTip.id))) {
            storageValue = storageValue
                ? storageValue + ' ,' + this.currentTip.id
                : String(this.currentTip.id);
            localStorage.setItem('optOutTipPopup', storageValue);
        }

        this.flyooutElement.classList.remove('open');
    };

    private fetchTips = async () => {
        await fetch(this.data.tipApiUrl)
            .then(res => res.json())
            .then(
                data => (this.tips = data._embedded['rh:doc'][0]),
                err => console.error(err)
            )
            .then(this.filterTips)
            .catch(err => console.error(err));
    };

    private checkTipUrl = (page: string): boolean => {
        const pagePathname = page.split('?')[0];
        const pageParams = page.split('?')[1];
        let matched = false;

        console.log(
            'checkTipUrl',
            page,
            window.location.href.toLowerCase().includes(pagePathname.toLowerCase())
        );

        if (window.location.href.toLowerCase().includes(pagePathname.toLowerCase())) {
            if (pageParams) {
                matched =
                    pageParams
                        .split('&')
                        .filter(param => window.location.search.toLowerCase().includes(param.toLowerCase()))
                        .length === pageParams.split('&').length;
            } else {
                matched = true;
            }
        }

        return matched;
    };

    private filterTips = () => {
        console.log('filter tips');
        this.currentTip = this.tips?.tips
            .map(tip => tip)
            .filter(
                tip =>
                    this.tips.fs_date > tip.start &&
                    this.tips.fs_date < tip.end &&
                    tip.pages.filter(page => this.checkTipUrl(page)).length > 0
            )[0];
    };

    private tipInitialTeaser = (duration: number): void => {
        setTimeout(() => {
            this.flyooutElement.classList.add('open');
            this.setActive('tip');
        }, 1000);
        setTimeout(() => {
            this.flyooutElement.classList.remove('open');
        }, duration);
    };

    render() {
        return (
            this.data && (
                <Host>
                    <div class="flyout" ref={el => (this.flyooutElement = el)}>
                        <sew-ws-floating-buttons-contact-options-option
                            class="overview active"
                            optionTitle={this.data.entranceTitle}
                            noBack={true}
                        >
                            <p>{this.data.entranceText1}</p>
                            <p>{this.data.entranceText2}</p>
                            <div class="options">
                                {this.enableChat && (
                                    <button
                                        class="option"
                                        onClick={() => {
                                            this.webchatToggleBtnRef.click();
                                            this.flyooutElement.classList.remove('open');
                                        }}
                                    >
                                        <sew-icon class="icon" icon="chat-bubbles" size="xl" />
                                        <div class="text">
                                            <div class="option-name">{this.data.chatBtnTitle}</div>
                                            <div class="option-description">{this.data.chatBtnText}</div>
                                        </div>
                                        <sew-icon class="arrow-svg" icon="arrow-right" size="m"></sew-icon>
                                    </button>
                                )}
                                <button
                                    class="option"
                                    onClick={() => {
                                        this.loadGoogleApi = true;
                                        this.setActive('contact');
                                    }}
                                >
                                    <sew-icon class="icon" icon="telephone" size="xl" />
                                    <div class="text">
                                        <div class="option-name">{this.data.contactTitle}</div>
                                        <div class="option-description">{this.data.contactText}</div>
                                    </div>
                                    <sew-icon class="arrow-svg" icon="arrow-right" size="m"></sew-icon>
                                </button>
                                {this.enableRecruiting && this.data.recruitingTitle && (
                                    <button
                                        class="option"
                                        onClick={() => {
                                            this.setActive('contact-recruiting');
                                        }}
                                    >
                                        <sew-icon class="icon" icon="telephone" size="xl" />
                                        <div class="text">
                                            <div class="option-name">{this.data.recruitingTitle}</div>
                                            <div class="option-description">{this.data.recruitingText}</div>
                                        </div>
                                        <sew-icon class="arrow-svg" icon="arrow-right" size="m"></sew-icon>
                                    </button>
                                )}
                                {this.enableDownloads && (
                                    <button
                                        class="option"
                                        onClick={() => {
                                            this.setActive('downloads');
                                        }}
                                    >
                                        <sew-icon class="icon" icon="download" size="xl" />
                                        <div class="text">
                                            <div class="option-name">{this.data.downloads.downloadTitle}</div>
                                            <div class="option-description">
                                                {this.data.downloads.downloadText}
                                            </div>
                                        </div>
                                        <sew-icon class="arrow-svg" icon="arrow-right" size="m"></sew-icon>
                                    </button>
                                )}
                                {this.enableWhatsapp && (
                                    <button
                                        class="option"
                                        onClick={() => {
                                            this.setActive('whatsapp');
                                        }}
                                    >
                                        <sew-icon class="icon" icon="logo-whatsapp" size="xl" />
                                        <div class="text">
                                            <div class="option-name">{this.data.whatsapp.headline}</div>
                                            <div class="option-description">{this.data.whatsapp.text}</div>
                                        </div>
                                        <sew-icon class="arrow-svg" icon="arrow-right" size="m"></sew-icon>
                                    </button>
                                )}
                            </div>
                        </sew-ws-floating-buttons-contact-options-option>
                        <sew-ws-floating-buttons-contact-options-option
                            class="contact"
                            optionTitle={this.data.contactTitle}
                            icon="contact-telephone"
                        >
                            <div class="card-content">
                                <div class="headline">{this.data.firstPhoneTitle}</div>
                                <a href={'tel:' + this.data.firstPhoneNumber} class="text">
                                    {this.data.firstPhoneNumber}
                                </a>
                                {this.data.firstPhoneHelpText && (
                                    <div class="help-text">{this.data.firstPhoneHelpText}</div>
                                )}
                            </div>
                            <hr />
                            <div class="card-content">
                                <div class="headline">{this.data.secondPhoneTitle}</div>
                                <a href={'tel:' + this.data.secondPhoneNumber} class="text">
                                    {this.data.secondPhoneNumber}
                                </a>
                                {this.data.secondPhoneHelpText && (
                                    <div class="help-text">{this.data.secondPhoneHelpText}</div>
                                )}
                            </div>
                            {this.data.thirdPhoneTitle && <hr />}
                            {this.data.thirdPhoneTitle && (
                                <div class="card-content">
                                    <div class="headline">{this.data.thirdPhoneTitle}</div>
                                    <a href={'tel:' + this.data.thirdPhoneNumber} class="text">
                                        {this.data.thirdPhoneNumber}
                                    </a>
                                    {this.data.thirdPhoneHelpText && (
                                        <div class="help-text">{this.data.thirdPhoneHelpText}</div>
                                    )}
                                </div>
                            )}
                            <div class="search-field">
                                {!this.data.externalZipcodeSearch &&
                                    this.getConsent() &&
                                    this.loadGoogleApi && (
                                        <sew-search-field-google-api
                                            apiKey={this.data.apiKey}
                                            localization={this.data.localization}
                                            apiBaseUrl={this.data.apiBaseUrl}
                                            placeholder={this.data.placeholder}
                                            isFixed
                                        ></sew-search-field-google-api>
                                    )}
                                {this.data.externalZipcodeSearch && !this.getConsent() && (
                                    <form action={this.data.externalZipcodeSearch}>
                                        <sew-search-field ws>
                                            <input
                                                type="text"
                                                name={this.data.externalZipcodeInputName}
                                                placeholder={this.data.placeholder}
                                            />
                                        </sew-search-field>
                                    </form>
                                )}
                            </div>
                            {this.data.externalZipcodeSearch && this.getConsent() && this.loadGoogleApi && (
                                <sew-search-field-google-api
                                    class="search-field"
                                    apiKey={this.data.apiKey}
                                    localization={this.data.localization}
                                    externalZipcodeSearch={this.data.externalZipcodeSearch}
                                    externalZipcodeInputName={this.data.externalZipcodeInputName}
                                    placeholder={this.data.placeholder}
                                    isFixed
                                ></sew-search-field-google-api>
                            )}
                            <sew-ws-linklist>
                                {this.data.links.map(item => (
                                    <sew-ws-link>
                                        <a
                                            href={item.link?.href}
                                            target={item.link?.target}
                                            data-sew-trackme-event-category={item.tracking.category}
                                            data-sew-trackme-event-object={item.tracking.object}
                                            data-sew-trackme-event-action={item.tracking.action}
                                        >
                                            {item.link?.text}
                                        </a>
                                    </sew-ws-link>
                                ))}
                            </sew-ws-linklist>
                        </sew-ws-floating-buttons-contact-options-option>
                        <sew-ws-floating-buttons-contact-options-option
                            class="contact-recruiting"
                            optionTitle={this.data.recruitingTitle}
                            icon="contact-telephone"
                        >
                            <div class="card-content">
                                <div class="headline">{this.data.recruitingFirstPhoneTitle}</div>
                                <a href={'tel:' + this.data.recruitingFirstPhoneNumber} class="text">
                                    {this.data.recruitingFirstPhoneNumber}
                                </a>
                                {this.data.recruitingFirstPhoneHelpText && (
                                    <div class="help-text">{this.data.recruitingFirstPhoneHelpText}</div>
                                )}
                            </div>
                            <hr />
                            <div class="card-content">
                                <div class="headline">{this.data.recruitingSecondPhoneTitle}</div>
                                <a href={'tel:' + this.data.recruitingSecondPhoneNumber} class="text">
                                    {this.data.recruitingSecondPhoneNumber}
                                </a>
                                {this.data.recruitingSecondPhoneHelpText && (
                                    <div class="help-text">{this.data.recruitingSecondPhoneHelpText}</div>
                                )}
                            </div>
                            {this.data.recruitingThirdPhoneTitle && <hr />}
                            {this.data.recruitingThirdPhoneTitle && (
                                <div class="card-content">
                                    <div class="headline">{this.data.recruitingThirdPhoneTitle}</div>
                                    <a href={'tel:' + this.data.recruitingThirdPhoneNumber} class="text">
                                        {this.data.recruitingThirdPhoneNumber}
                                    </a>
                                    {this.data.recruitingThirdPhoneHelpText && (
                                        <div class="help-text">{this.data.recruitingThirdPhoneHelpText}</div>
                                    )}
                                </div>
                            )}
                            <sew-ws-linklist>
                                {this.data.links.map(item => (
                                    <sew-ws-link>
                                        <a
                                            href={item.link?.href}
                                            target={item.link?.target}
                                            data-sew-trackme-event-category={item.tracking.category}
                                            data-sew-trackme-event-object={item.tracking.object}
                                            data-sew-trackme-event-action={item.tracking.action}
                                        >
                                            {item.link?.text}
                                        </a>
                                    </sew-ws-link>
                                ))}
                            </sew-ws-linklist>
                        </sew-ws-floating-buttons-contact-options-option>
                        {this.enableDownloads && (
                            <sew-ws-floating-buttons-contact-options-option
                                class="downloads"
                                optionTitle={this.data.downloads.downloadTitle}
                                icon="download"
                            >
                                <sew-ws-downloads
                                    download-types={JSON.stringify(this.data.downloads.downloadTypes)}
                                    button-text={this.data.downloads.buttonText}
                                    links-headline={this.data.downloads.linksHeadline}
                                    links-text={this.data.downloads.linksText}
                                    download-text={this.data.downloads.downloadText}
                                    search-input-label={this.data.searchInputLabel}
                                    tracking-category="Quicknavi"
                                    services-item-label="Data and Documents"
                                    allow-search-input
                                    class="inside-flyout"
                                />
                            </sew-ws-floating-buttons-contact-options-option>
                        )}
                        {this.enableWhatsapp && (
                            <sew-ws-floating-buttons-contact-options-option
                                class="whatsapp"
                                optionTitle={this.data.whatsapp.headline}
                                icon="logo-whatsapp"
                            >
                                <sew-ws-whatsapp-service
                                    items={this.data.whatsapp.items}
                                ></sew-ws-whatsapp-service>
                            </sew-ws-floating-buttons-contact-options-option>
                        )}
                        {this.enableTips && this.currentTip && (
                            <sew-ws-floating-buttons-contact-options-option
                                class="tip"
                                optionTitle={this.currentTip.title}
                                icon="highlight"
                                noBack={true}
                                closeCallback={() => {
                                    this.optOutCurrentTipPopup();
                                }}
                            >
                                <img
                                    loading="lazy"
                                    src={this.currentTip.image_src}
                                    alt={this.currentTip.image_alt}
                                    title={this.currentTip.image_title}
                                />
                                <p class="highlight-headline">{this.currentTip.headline}</p>
                                <p class="highlight-text">{this.currentTip.text}</p>
                                <sew-ws-button class="tip-btn">
                                    <a
                                        href={this.currentTip.button_link}
                                        target={this.currentTip.link_target}
                                    >
                                        {this.currentTip.button_text}
                                    </a>
                                </sew-ws-button>
                                <sew-ws-link class="tip-opt-out-link">
                                    <button onClick={this.optOutInitialTipAnimation}>
                                        {this.data.tipOptOutLabel}
                                    </button>
                                </sew-ws-link>
                            </sew-ws-floating-buttons-contact-options-option>
                        )}
                    </div>
                    <div
                        class={{
                            'floating-buttons': true,
                            'scroll-to-top-visible': this.isScrollToTopVisible,
                        }}
                    >
                        {this.webchatInitialized && (
                            <button
                                class="chat-btn"
                                aria-label="webchat"
                                onClick={() => this.webchatToggleBtnRef.click()}
                            >
                                <div class="hover">
                                    <div class="title">{this.data.chatBtnTitle}</div>
                                    <div class="text">{this.data.chatBtnText}</div>
                                </div>
                                <div class="icon">
                                    <sew-icon class="svg" icon="chat-bubbles" size="xl" />
                                </div>
                            </button>
                        )}
                        {this.enableLinkButton && (
                            <a class="link-btn" aria-label={this.linkBtnText} href={this.linkBtnLink}>
                                <div class="hover">
                                    <div class="text">{this.linkBtnText}</div>
                                </div>
                                <div class="icon">
                                    <sew-icon class="svg" icon={this.linkBtnIcon} size="xl" />
                                </div>
                            </a>
                        )}
                        {this.enableTips && this.currentTip && (
                            <sew-ws-tip-button
                                onClickedButton={() => {
                                    this.flyooutElement.classList.add('open');
                                    this.setActive('tip');
                                }}
                                tip-label={this.data.tipLabel}
                                initial-animation={!this.hasBeenShown}
                            />
                        )}
                        <button
                            class="contact-options-btn"
                            onClick={() => {
                                this.flyooutElement.classList.add('open');
                                this.setActive('overview');
                            }}
                        >
                            <div class="text">
                                <div class="text1">{this.data.contactMainBtnTitle}</div>
                                <div class="text2">{this.data.contactMainBtnText}</div>
                            </div>
                            <img src={this.data.contactMainBtnImageSrc} />
                        </button>
                        <button
                            class="scroll-to-top-btn"
                            onClick={this.scrollToTop}
                            aria-label={this.data.ariaLabelScrollToTop}
                        >
                            <sew-icon class="svg" icon="arrow-up" size="xl" />
                        </button>
                    </div>
                </Host>
            )
        );
    }
}
